/**
 * @fileoverview A public page's switch and its numbers (the open count beside
 * Share), one read for a plant, a bed or a season review. A plant card, a bed
 * card and the season review page all asked it by hand with their own key and
 * stale time; the Public switch (features/share/PublicPageSwitch) reads the same
 * keys, so a change made there is the count here. A bed card reads it only
 * when the bed list could not carry the bed's sharing (null; W24 Job H): the
 * list carries it, and the card hands it to the switch, so ten beds make no
 * share read; the plant card has the plants brief's (B110).
 *
 * Also the two ways a card says the count, shared by the plant card's and the
 * bed card's Share (opensWords). Words (en/es): plants.card.public.visits and
 * visits_one.
 *
 * Where: SeasonReviewPage; LocationCard (only when the bed list's sharing is null).
 * API: GET /shares/plant/:id, /shares/location/:id, /shares/season/:year
 * (getSubjectSharing, getSeasonSharing).
 * Settings: none; the query keys and the minute a count stays fresh are this hook's own, the same words
 * the Public switch (PublicPageSwitch) reads its numbers under.
 *
 * @module useShareCount
 */

import { useQuery } from '@tanstack/react-query';
import { getSeasonSharing, getSubjectSharing, type PlantSharing } from '@/shared/api/shares';
import type { Subject } from '@/shared/api/subject';

/** How long a count stays fresh before it is read again, in milliseconds. */
const SHARE_COUNT_STALE_MS = 60_000;

/** What has a public page and links: a plant, a bed, or a season review (its id is the year). */
export type ShareSubject = Subject | { kind: 'season'; id: number };

/**
 * How a card says a share's open count: the short number on the Share button's
 * corner and the sentence for its tooltip and label.
 *
 * @param opens - How many times the links were opened.
 * @param language - The reader's language, for the number's compact form.
 * @param t - The translate function.
 * @returns `short` ("8", "950", "1.2K"; null for none) and `phrase` ("opened 1 time", "opened 3 times"; null for none).
 */
export function opensWords(opens: number, language: string, t: (key: string, params?: Record<string, string | number>) => string): { short: string | null; phrase: string | null } {
  if (opens <= 0) return { short: null, phrase: null };
  return {
    short: new Intl.NumberFormat(language, { notation: 'compact', maximumFractionDigits: 1 }).format(opens),
    phrase: opens === 1 ? t('plants.card.public.visits_one') : t('plants.card.public.visits', { count: opens }),
  };
}

/**
 * The query key of a subject's sharing numbers; a writer that changes the
 * switch sets or refreshes this key.
 *
 * @param subject - The plant, bed or season.
 * @returns The key.
 */
export function shareCountKey(subject: ShareSubject): readonly [string, number] {
  const name = subject.kind === 'plant' ? 'plant-sharing'
    : subject.kind === 'location' ? 'location-sharing'
      : 'season-sharing';
  return [name, subject.id];
}

/**
 * Reads a subject's sharing numbers.
 *
 * @param subject - The plant, bed or season.
 * @param enabled - Whether to read now (a card with nothing to share waits); true by default.
 * @returns `sharing` (the switch and numbers; undefined until read), `opens` (how many times its links were opened; 0 until the count is ready) and the read's `error`.
 */
export function useShareCount(subject: ShareSubject, enabled = true): { sharing: PlantSharing | undefined; opens: number; error: Error | null } {
  const { data, error } = useQuery({
    queryKey: shareCountKey(subject),
    queryFn: () => (subject.kind === 'season' ? getSeasonSharing(subject.id) : getSubjectSharing(subject)),
    staleTime: SHARE_COUNT_STALE_MS,
    enabled,
  });
  return { sharing: data, opens: data ? data.visits : 0, error };
}
